/*
 * Всплывающие уведомления.
 *
 * Единственный способ сообщить о том, что произошло вне поля зрения:
 * сообщение удалено, ссылка скопирована, сеть отвалилась. Модальное окно
 * для этого слишком тяжёлое, а молчание оставляет пользователя в неведении.
 */

export type ToastKind = 'info' | 'success' | 'error';

export interface Toast {
	id: string;
	kind: ToastKind;
	text: string;
	/** Действие в уведомлении — например, «Отменить» у удаления. */
	action?: { label: string; run: () => void };
}

/** Сколько уведомление висит на экране, мс. */
const LIFETIME = 4000;

/** Больше трёх одновременно — это уже стена текста, а не уведомления. */
const MAX_VISIBLE = 3;

class ToastState {
	items = $state<Toast[]>([]);

	private timers = new Map<string, ReturnType<typeof setTimeout>>();

	show(text: string, kind: ToastKind = 'info', action?: Toast['action']): string {
		const id = crypto.randomUUID();

		this.items = [...this.items, { id, kind, text, action }].slice(-MAX_VISIBLE);
		this.timers.set(
			id,
			setTimeout(() => this.dismiss(id), LIFETIME)
		);

		return id;
	}

	success(text: string, action?: Toast['action']): string {
		return this.show(text, 'success', action);
	}

	error(text: string, action?: Toast['action']): string {
		return this.show(text, 'error', action);
	}

	dismiss(id: string): void {
		const timer = this.timers.get(id);
		if (timer) {
			clearTimeout(timer);
			this.timers.delete(id);
		}

		this.items = this.items.filter((t) => t.id !== id);
	}

	/**
	 * Запускает действие уведомления и сразу его закрывает: после нажатия
	 * «Отменить» держать уведомление на экране незачем.
	 */
	runAction(id: string): void {
		const toast = this.items.find((t) => t.id === id);
		toast?.action?.run();
		this.dismiss(id);
	}
}

export const toasts = new ToastState();
